/**
 * Protected Route Component
 * 
 * Wraps routes that require authentication.
 * Redirects to login if user is not authenticated.
 * Optionally restricts to admin-only routes.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useAuth } from '../../context/AuthContext';

/**
 * ProtectedRoute - Guards routes that require authentication
 * 
 * @param {object} props
 * @param {React.ReactNode} props.children - The protected content
 * @param {boolean} props.requireAdmin - If true, only admins can access
 * @param {string} props.redirectTo - Where to redirect if not authenticated
 * @param {React.ReactNode} props.fallback - Loading state component
 */
const ProtectedRoute = ({ 
  children, 
  requireAdmin = false, 
  redirectTo = '/login',
  fallback = null
}) => {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  if (isLoading) {
    if (fallback) {
      return fallback;
    }
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600"></div>
      </div>
    );
  }

  // Not authenticated - redirect to login with return URL
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Requires admin but user is not admin - redirect to home
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/shop" replace />;
  }

  // Authenticated (and admin if required) - render children
  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requireAdmin: PropTypes.bool,
  redirectTo: PropTypes.string,
  fallback: PropTypes.node
};

/**
 * PublicOnlyRoute - Guards routes that should only be accessible when NOT logged in
 * (e.g., login page, register page)
 * 
 * @param {object} props
 * @param {React.ReactNode} props.children - The content
 * @param {string} props.redirectTo - Where to redirect if authenticated
 */
export const PublicOnlyRoute = ({ 
  children, 
  redirectTo = '/shop' 
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Already authenticated - redirect (use 'from' location if available)
  if (isAuthenticated) {
    const from = location.state?.from?.pathname || redirectTo;
    return <Navigate to={from} replace />;
  }

  // Not authenticated - show the public content
  return children;
};

PublicOnlyRoute.propTypes = {
  children: PropTypes.node.isRequired,
  redirectTo: PropTypes.string
};

export default ProtectedRoute;
